/**
 * PATH: apps/quote/src/pages/pricelist-page/ItemEditorModal.tsx
 * PURPOSE: Create/edit a single PriceBook item (wires the previously-stubbed
 *          "Add Item" + row "Edit" buttons on the Pricelist page).
 * WHY: Operator audit 2026-06-14 — those buttons rendered but did nothing.
 * FLOW: form state → onSave(data, itemId?) → quoteApi.create/updatePricebookItem
 * RELATED: usePricelistData (mutations), ItemsTab (edit trigger), PricelistPage
 */
import { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import type { PriceBookItem, CreatePriceBookItemRequest } from '@/lib/quoteApi';
import ItemAttachmentsPanel from './ItemAttachmentsPanel';

const ITEM_TYPES = ['material', 'labour', 'service', 'plant', 'subcontractor', 'other'];
const UNITS = ['m2', 'lm', 'unit', 'each', 'roll', 'kit', 'kg', 'litre', 'day', 'hour'];

interface ItemEditorModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  item?: PriceBookItem | null;
  pricebookId?: string;
  saving: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (data: CreatePriceBookItemRequest, itemId?: string) => void;
}

type FormState = {
  item_code: string;
  item_name: string;
  description: string;
  item_type: string;
  unit: string;
  base_cost: string;
  pricing_method: string;
  markup_percent: string;
  fixed_sell_price: string;
};

const EMPTY: FormState = {
  item_code: '', item_name: '', description: '', item_type: 'material',
  unit: 'unit', base_cost: '0', pricing_method: 'markup', markup_percent: '25', fixed_sell_price: '',
};

function fromItem(item: PriceBookItem): FormState {
  return {
    item_code: item.item_code ?? '',
    item_name: item.item_name ?? '',
    description: item.description ?? '',
    item_type: item.item_type ?? 'material',
    unit: item.unit ?? 'unit',
    base_cost: String(item.base_cost ?? '0'),
    pricing_method: item.pricing_method ?? 'markup',
    markup_percent: String(item.markup_percent ?? '25'),
    fixed_sell_price: item.fixed_sell_price != null ? String(item.fixed_sell_price) : '',
  };
}

export default function ItemEditorModal({ open, mode, item, pricebookId, saving, error, onClose, onSave }: ItemEditorModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(mode === 'edit' && item ? fromItem(item) : EMPTY);
  }, [open, mode, item]);

  if (!open) return null;

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSave = form.item_code.trim().length > 0 && form.item_name.trim().length > 0 && !saving;

  const submit = () => {
    if (!canSave) return;
    const payload: CreatePriceBookItemRequest = {
      item_code: form.item_code.trim(),
      item_name: form.item_name.trim(),
      description: form.description.trim() || undefined,
      item_type: form.item_type,
      unit: form.unit,
      base_cost: form.base_cost === '' ? 0 : Number(form.base_cost),
      pricing_method: form.pricing_method,
      markup_percent: form.markup_percent === '' ? 0 : Number(form.markup_percent),
      fixed_sell_price: form.pricing_method === 'fixed' && form.fixed_sell_price !== ''
        ? Number(form.fixed_sell_price)
        : null,
    };
    onSave(payload, mode === 'edit' ? item?.id : undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {mode === 'edit' ? 'Edit item' : 'Add item'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 px-5 py-4">
          <Field label="Code *">
            <input value={form.item_code} onChange={set('item_code')} className={inputCls} placeholder="e.g. MEM-001" />
          </Field>
          <Field label="Type">
            <select value={form.item_type} onChange={set('item_type')} className={inputCls}>
              {ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Name *" full>
            <input value={form.item_name} onChange={set('item_name')} className={inputCls} placeholder="Item name" />
          </Field>
          <Field label="Description" full>
            <textarea value={form.description} onChange={set('description')} rows={2} className={inputCls} />
          </Field>
          <Field label="Unit">
            <select value={form.unit} onChange={set('unit')} className={inputCls}>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Cost">
            <input type="number" value={form.base_cost} onChange={set('base_cost')} className={inputCls} />
          </Field>
          <Field label="Pricing">
            <select value={form.pricing_method} onChange={set('pricing_method')} className={inputCls}>
              <option value="markup">Markup %</option>
              <option value="fixed">Fixed sell price</option>
            </select>
          </Field>
          {form.pricing_method === 'fixed' ? (
            <Field label="Sell price">
              <input type="number" value={form.fixed_sell_price} onChange={set('fixed_sell_price')} className={inputCls} />
            </Field>
          ) : (
            <Field label="Markup %">
              <input type="number" value={form.markup_percent} onChange={set('markup_percent')} className={inputCls} />
            </Field>
          )}
        </div>

        {mode === 'edit' && item?.id && pricebookId && (
          <ItemAttachmentsPanel pricebookId={pricebookId} itemId={item.id} />
        )}

        {error && (
          <div className="mx-5 mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 dark:border-gray-700 px-5 py-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'edit' ? 'Save changes' : 'Add item'}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm';

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? 'col-span-2' : ''}`}>
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}
